/**
 * Tour step registry for the v2 walkthrough (#1261, #1262).
 *
 * Single source of truth for step → anchor mapping. Anchors are stable
 * `data-tour-anchor` attributes (not class selectors) so layout refactors
 * don't break the tour. Each step also carries its tooltip copy keys for
 * i18n.
 *
 * Step phasing per design handoff §2:
 *   Phase 1 (pre_scenario):   1, 2  → fire on dashboard
 *   Phase 2 (post_scenario):  3     → fires inline on AssessmentResults
 *   Phase 2 (post_scenario):  4–6   → fire on dashboard / nav after step 3
 */

// v1 tour: 7 steps. Post-scenario flow walks the learner through the
// coaching view's two panels + close X (steps 2–4 on /chat/:id?view=
// coaching), then over to the dashboard nav cards (steps 5–7).
//
// Why not anchor step 2 on the .score-value inside the eval column?
// The coaching chat panel auto-scrolls to bottom on every new AI
// message, which fights with the tour's scrollIntoView when the
// anchor is INSIDE a scrollable inner column. By anchoring on the
// column itself (always at top: 73, not internally-scrolled), we
// avoid the scroll fight. The coachmark copy still describes the
// score + feedback content the learner finds inside the column.
//
// Cross-page transition: step 4 anchors on .coaching-close-btn — the
// X button that natively navigates back to /dashboard. On the next
// /dashboard render, the anchor poll picks up step 5 automatically.
// No special handling in the tour engine.
export const TOUR_STEPS = [
  {
    step: 1,
    phase: 'pre_scenario',
    anchor: 'welcome-scenario-card',
    placement: 'bottom',
    // Anchor stays clickable — clicking the welcome card directly is a
    // valid way to start the scenario alongside the coachmark's primary CTA.
    interactive: true,
    titleKey: 'onboarding.tour.step1.title',
    bodyKey: 'onboarding.tour.step1.body',
    titleDefault: 'Start with this one.',
    bodyDefault:
      "We've picked a 5-minute welcome scenario to ease you in. Click Start when you're ready — we'll pick the tour back up after your debrief.",
  },
  {
    step: 2,
    phase: 'post_scenario',
    anchor: 'debrief-feedback',
    placement: 'right',
    // Eval column scrolls internally to show the full breakdown — the
    // copy explicitly tells the learner to "scroll for the full
    // breakdown", so a click/scroll shield would defeat the prompt.
    interactive: true,
    titleKey: 'onboarding.tour.step2.title',
    bodyKey: 'onboarding.tour.step2.body',
    titleDefault: 'Score and feedback land here.',
    bodyDefault:
      "After every scenario, you'll get a score and a quick read on what to work on. Scroll for the full breakdown.",
    // #2796: this anchor ('debrief-feedback') is shared by every debrief_mode
    // — on an `artifact_review` scenario the column it spotlights holds the
    // generated artifact, not a score (AssessmentResults never mounts there;
    // see HelpPanelCoachingView's `activeTab === 'evaluation' &&
    // !isArtifactReview` gate). resolveStep() below swaps this step's copy
    // for the variant when the caller reports artifact_review is live —
    // steps 3/4 read correctly in both modes and are NOT overridden.
    artifactReviewVariant: {
      titleKey: 'onboarding.tour.step2.titleArtifactReview',
      bodyKey: 'onboarding.tour.step2.bodyArtifactReview',
      titleDefault: 'Your brief lands here.',
      bodyDefault:
        "Instead of a score, you'll get a written brief from the conversation to keep. Scroll for the full read.",
    },
  },
  {
    step: 3,
    phase: 'post_scenario',
    anchor: 'debrief-coach',
    placement: 'left',
    // Coach column needs keyboard focus + typing into the textarea.
    // Shield would block focus changes and reject keystrokes.
    interactive: true,
    titleKey: 'onboarding.tour.step3.title',
    bodyKey: 'onboarding.tour.step3.body',
    titleDefault: 'Talk it through with the coach.',
    bodyDefault:
      'The coach walks you through what happened and helps you plan your next step.',
  },
  {
    step: 4,
    phase: 'post_scenario',
    anchor: 'debrief-close',
    // Anchor is top-right X — 'bottom' centered the coachmark on the X
    // and pushed it off-screen on the right. 'left' tucks it inside the
    // viewport (auto-flip to bottom-docked still kicks in on mobile).
    placement: 'left',
    titleKey: 'onboarding.tour.step4.title',
    bodyKey: 'onboarding.tour.step4.body',
    titleDefault: 'Well done — your first scenario is done.',
    bodyDefault:
      'Head back to the dashboard. We\'ll show you a few more places worth knowing about.',
  },
  {
    step: 5,
    phase: 'post_scenario',
    anchor: 'nav-progress',
    placement: 'bottom',
    titleKey: 'onboarding.tour.step5.title',
    bodyKey: 'onboarding.tour.step5.body',
    titleDefault: "Track how you're growing.",
    bodyDefault:
      "My Progress shows scores over time, strengths, and skills you're working on. Come back as often as you like.",
  },
  {
    step: 6,
    phase: 'post_scenario',
    anchor: 'nav-community',
    placement: 'bottom',
    titleKey: 'onboarding.tour.step6.title',
    bodyKey: 'onboarding.tour.step6.body',
    titleDefault: 'Borrow from your peers.',
    bodyDefault:
      'Community has scenarios shared by other learners and our curated catalogue. A great place to find something challenging.',
  },
  {
    step: 7,
    phase: 'post_scenario',
    anchor: 'nav-build',
    placement: 'bottom',
    titleKey: 'onboarding.tour.step7.title',
    bodyKey: 'onboarding.tour.step7.body',
    titleDefault: 'Got a real conversation coming up?',
    bodyDefault:
      'Build your own scenario from a script or a few sentences — and practise it before the real thing.',
  },
  {
    step: 8,
    phase: 'post_scenario',
    anchor: 'coach-card',
    placement: 'top',
    // Closing step: the dashboard's CoachCard is the product's "what to
    // do next" answer for every learner — recommendations + streaks +
    // resume-coaching CTAs. Works whether the carousel is full of
    // assignments (Umuzi cohort) or empty (trial/self-discovery learner)
    // because CoachCard renders for both. Pointing the tour at it lands
    // the learner on a concrete next action instead of leaving them on
    // the abstract "Build your own" Tier-3 entry.
    titleKey: 'onboarding.tour.step8.title',
    bodyKey: 'onboarding.tour.step8.body',
    titleDefault: 'And when you\'re ready for more — start here.',
    bodyDefault:
      'Your coach card picks up where you left off and suggests what to practise next. Open it any time you\'re unsure what to do next.',
  },
];

export const TOTAL_STEPS = TOUR_STEPS.length;

export function getStep(stepNumber) {
  return TOUR_STEPS.find((s) => s.step === stepNumber) || null;
}

/**
 * resolveStep — like getStep, but swaps in a step's `artifactReviewVariant`
 * (currently only step 2 has one; #2796) when the caller reports the live
 * debrief is `artifact_review`. Pure function so the mode-aware selection is
 * testable without mounting the tour engine.
 */
export function resolveStep(stepNumber, { isArtifactReviewDebrief = false } = {}) {
  const step = getStep(stepNumber);
  if (!step) return null;
  if (isArtifactReviewDebrief && step.artifactReviewVariant) {
    return { ...step, ...step.artifactReviewVariant };
  }
  return step;
}

export function getStepsForPhase(phase) {
  return TOUR_STEPS.filter((s) => s.phase === phase);
}

/**
 * Resolve an anchor element by its `data-tour-anchor` attribute. Returns
 * null if the element isn't in the DOM yet (e.g. learner not on the right
 * route, or layout hasn't mounted). Caller is expected to retry on the
 * next render or fall back gracefully.
 *
 * A single anchor key can legitimately match more than one element at once
 * — e.g. `debrief-close` is carried by both the four-act header's "Exit"
 * button (hidden via `display:none` at <=767px, App.css) and Act 4's own
 * "Done" button (DebriefActs.jsx, #2824) — and a bare `querySelector`
 * always wins with the FIRST DOM match regardless of which one is actually
 * visible. Because the header renders earlier in ChatPage's tree than
 * DebriefActs, that first match was the display:none header button on
 * mobile: its zero-size rect made TourProvider's inViewport() check
 * trivially true, so the tour never noticed the anchor was invisible
 * (#2874 review-fix, findings for #2819+#2824). Prefer whichever matching
 * element actually has on-screen size; fall back to the first match (old
 * behaviour) only when none of the candidates are visible yet, so a
 * genuinely-not-mounted anchor still resolves once it appears.
 */
export function findAnchor(anchorKey) {
  if (typeof document === 'undefined') return null;
  const matches = document.querySelectorAll(`[data-tour-anchor="${anchorKey}"]`);
  if (matches.length === 0) return null;
  for (const el of matches) {
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 || rect.height > 0) return el;
  }
  return matches[0];
}

/**
 * Whether `el` can ever be painted: false when the element or any ancestor
 * has computed `display: none` (this also covers the `hidden` attribute,
 * which computes to `display: none`). A zero-size rect is NOT hidden here:
 * a node that simply has not been laid out yet must keep resolving, so the
 * only thing this separates from that case is "in the DOM but never going to
 * render at this breakpoint" (#3475 Q2874-1: the four-act header "Exit"
 * button is `display:none` at <=767px while Act 4's "Done" is not mounted
 * yet).
 *
 * Fail-open: if `getComputedStyle` is unavailable or throws, answer true so
 * an unsupported environment keeps the pre-existing behaviour.
 */
export function isAnchorRendered(el) {
  if (!el) return false;
  try {
    if (typeof getComputedStyle !== 'function') return true;
    for (let node = el; node; node = node.parentElement) {
      if (getComputedStyle(node).display === 'none') return false;
    }
    return true;
  } catch {
    return true;
  }
}
